import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { createReturnDraftLine } from '#/modules/imsMgmt/receipts/stockReturn/helper/formSerialize';

function StatCell({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-base font-semibold text-gray-900">{value}</p>
    </div>
  );
}

StatCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function sourceTypeLabel(sourceType) {
  if (sourceType === 'PENDING_ALLOCATION') {
    return '待分配';
  }
  return '庫存';
}

function formatPlaceLabel(row) {
  return [row?.warehouseLabel, row?.locationLabel].filter(Boolean).join(' / ')
    || '（無儲位）';
}

/**
 * 退料選庫。僅勾選；確認後 qty＝可用量。
 * 舊「dialog 內編數量」見 ORD-stock-return-picker-qty-sync.md。
 */
const StockReturnPickerDialog = ({
  open,
  onOpenChange,
  material,
  initialSelection = {},
  onConfirm,
}) => {
  /** null＝本次開啟尚未編輯，直接沿用 draft 帶入值（勿用 effect 同步）。 */
  const [selectionDraft, setSelectionDraft] = useState(null);
  const sourceRows = useMemo(
    () => material?.sources ?? [],
    [material],
  );
  const selection = selectionDraft ?? initialSelection;
  const [candidateKeyword, setCandidateKeyword] = useState('');

  const operatingQty = useMemo(
    () => sourceRows
      .filter((row) => row.selectable && selection[row.id] != null)
      .reduce((sum, row) => sum + (Number(row.availableQty) || 0), 0),
    [sourceRows, selection],
  );

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      setSelectionDraft(null);
      setCandidateKeyword('');
    }
    onOpenChange(nextOpen);
  }

  const filteredSourceRows = useMemo(() => {
    const q = candidateKeyword.trim().toLowerCase();
    const filtered = !q
      ? sourceRows
      : sourceRows.filter((row) => {
        const stockNo = row.stockNo ?? '';
        const invId = row.id ?? '';
        const place = formatPlaceLabel(row);
        return [
          String(stockNo),
          String(invId),
          String(place),
        ]
          .join(' ')
          .toLowerCase()
          .includes(q);
      });

    return [...filtered].sort((a, b) => (
      String(a?.stockNo ?? '').localeCompare(
        String(b?.stockNo ?? ''),
        undefined,
        { numeric: true, sensitivity: 'base' },
      )
    ));
  }, [candidateKeyword, sourceRows]);

  function handleToggle(row, checked) {
    if (!row?.selectable) {
      return;
    }
    const invId = row.id;
    setSelectionDraft((prev) => {
      const next = { ...(prev ?? initialSelection) };
      if (checked) {
        next[invId] = true;
      } else {
        delete next[invId];
      }
      return next;
    });
  }

  function handleConfirm() {
    const lines = sourceRows
      .filter((row) => row.selectable && selection[row.id] != null)
      .map((row) => createReturnDraftLine({
        mtrId: material.mtrId,
        mtrNo: material.mtrNo,
        mtrName: material.mtrName,
        ...(row.sourceType === 'PENDING_ALLOCATION'
          ? { pendingAllocationId: row.id }
          : { invStockId: row.id }),
        stockNo: row.stockNo,
        availableQty: row.availableQty,
        fromWarehouseLabel: formatPlaceLabel(row),
      }))
      .filter((line) => line && line.qty > 0);

    onConfirm(lines);
    setSelectionDraft(null);
    onOpenChange(false);
  }

  const title = material
    ? `選庫 — ${material.mtrName || material.mtrNo || material.mtrId}`
    : '選庫';

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2">
      <TableSearchInput
        useKeyword
        keyword={candidateKeyword}
        onKeywordChange={setCandidateKeyword}
        placeholder="搜尋庫存編號或儲位"
      />
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description="庫存與待分配來源皆可勾選；確認後以可用量寫入右側明細（可再於明細調整數量）。待分配來源的目標倉須與來源倉不同。"
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={() => handleOpenChange(false)}
      onConfirm={handleConfirm}
      confirmText="確認"
      cancelText="取消"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-2 sm:max-w-xs sm:grid-cols-2">
          <StatCell label="可用量合計" value={material?.onHandQty ?? '—'} />
          <StatCell label="操作中數量" value={operatingQty} />
        </div>

        {sourceRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">尚無可選庫存。</p>
        ) : filteredSourceRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">無符合篩選條件的庫存。</p>
        ) : (
          <ul className="m-0 list-none divide-y divide-slate-200 rounded-md border border-slate-200 p-0">
            {filteredSourceRows.map((row) => {
              const selectable = Boolean(row.selectable);
              const checked = selectable && selection[row.id] != null;
              const availableQty = Number(row.availableQty) || 0;
              return (
                <li
                  key={`${row.sourceType}-${row.id}`}
                  role="option"
                  aria-selected={checked}
                  aria-disabled={!selectable}
                  className={cn(
                    'grid grid-cols-1 gap-2 px-3 py-3 transition-colors',
                    'sm:grid-cols-[auto_minmax(0,1fr)_auto]',
                    'sm:items-center',
                    selectable ? 'cursor-pointer' : 'cursor-default opacity-80',
                    checked
                      ? 'bg-primary-100 shadow-inner hover:bg-primary-100'
                      : selectable
                        ? 'hover:bg-slate-50'
                        : 'bg-slate-50/80',
                  )}
                  onClick={() => {
                    if (selectable) {
                      handleToggle(row, !checked);
                    }
                  }}
                >
                  <div
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={!selectable}
                      onCheckedChange={(value) => handleToggle(row, value === true)}
                      aria-label={`選取 ${row.stockNo}`}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-mono text-sm text-gray-800">
                      {row.stockNo}
                      <span className="ml-2 font-sans text-xs text-muted-foreground">
                        {sourceTypeLabel(row.sourceType)}
                      </span>
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {formatPlaceLabel(row)}
                      {row.unitLabel ? ` · ${row.unitLabel}` : ''}
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground sm:text-right">
                    可用量：
                    <span className="text-base font-medium text-slate-800 tabular-nums">
                      {availableQty || '—'}
                    </span>
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AppDialog>
  );
};

StockReturnPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  material: PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    onHandQty: PropTypes.number,
    sources: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      stockNo: PropTypes.string,
      sourceType: PropTypes.string,
      selectable: PropTypes.bool,
      warehouseLabel: PropTypes.string,
      locationLabel: PropTypes.string,
      unitLabel: PropTypes.string,
      availableQty: PropTypes.number,
    })),
  }),
  initialSelection: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.bool, PropTypes.string]),
  ),
  onConfirm: PropTypes.func.isRequired,
};

export default StockReturnPickerDialog;
